import React, { useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { BASE_URL } from "../../config";
import "../Registration/Engineer.css";

function EngineerPayment() {
  const token = JSON.parse(localStorage.getItem("NagpurProperties"))?.token;

  const [paymentMonth, setPaymentMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // ── Modal state ──────────────────────────────
  const [modal, setModal] = useState(null);
  const [amountPaid, setAmountPaid] = useState("");
  const [remarks, setRemarks] = useState("");
  const [modalLoading, setModalLoading] = useState(false);

  // ── Check Salary Modal ───────────────────────
  const [salaryModal, setSalaryModal] = useState(false);
  const [salaryData, setSalaryData] = useState([]);
  const [salaryLoading, setSalaryLoading] = useState(false);

  // ─── Bulk generate ───────────────────────────
  const generatePayments = async () => {
    if (!paymentMonth) { alert("Please select payment month"); return; }
    const formattedMonth = paymentMonth.slice(0, 7) + "-01";

    setLoading(true);
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/engineer/payment/bulk-generate`,
        null,
        {
          params: { paymentMonth: formattedMonth },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setData(response.data);
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to generate payments";
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Per-engineer Payable ────────────────────
  const handlePayable = async (payment) => {
    setModalLoading(true);
    try {
      const formattedMonth = paymentMonth.slice(0, 7) + "-01";

      const response = await axiosInstance.post(
        `${BASE_URL}/engineer/${payment.engineerId}/payment/generate`,
        null,
        {
          params: { paymentMonth: formattedMonth },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const updated = response.data;
      setData((prev) => ({
        ...prev,
        engineerPayments: prev.engineerPayments.map((p) =>
          p.engineerId === updated.engineerId ? { ...p, ...updated } : p
        ),
      }));
      alert(`Payment generated for ${payment.engineerName}`);
    } catch (error) {
      console.error("Payable error:", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to generate payment";
      alert(message);
    } finally {
      setModalLoading(false);
    }
  };

  // ─── Check Salary ─────────────────────────────
  const handleCheckSalary = async () => {
    if (!paymentMonth) { alert("Please select payment month first"); return; }
    const formattedMonth = paymentMonth.slice(0, 7) + "-01";

    setSalaryLoading(true);
    setSalaryModal(true);
    setSalaryData([]);
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/engineer/payment/month`,
        {
          params: { paymentMonth: formattedMonth },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSalaryData(response.data);
    } catch (error) {
      console.error("Check salary error:", error);
      const message = error?.response?.data?.message || "Failed to fetch salary data";
      alert(message);
      setSalaryModal(false);
    } finally {
      setSalaryLoading(false);
    }
  };

  // ─── Open Paid modal ─────────────────────────
  const openPaidModal = (payment) => {
    setModal(payment);
    setAmountPaid("");
    setRemarks("");
  };

  const closePaidModal = () => {
    setModal(null);
    setAmountPaid("");
    setRemarks("");
  };

  // ─── Submit Paid ─────────────────────────────
  const handleRecordPayment = async () => {
    if (!amountPaid) { alert("Please enter amount"); return; }

    const enteredAmount = parseFloat(amountPaid);
    const pendingAmount = parseFloat(modal.pendingAmount);

    // ✅ Block overpayment
    if (enteredAmount > pendingAmount) {
      alert(`Amount cannot exceed pending amount of ₹ ${pendingAmount}`);
      return;
    }

    if (enteredAmount <= 0) {
      alert("Amount must be greater than 0");
      return;
    }

    const formattedMonth = paymentMonth.slice(0, 7) + "-01";

    setModalLoading(true);
    try {
      await axiosInstance.post(
        `${BASE_URL}/engineer/payment/record`,
        {
          engineerId: modal.engineerId,
          paymentMonth: formattedMonth,
          amountPaid: enteredAmount,
          remarks,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Payment recorded successfully");
      closePaidModal();
      await generatePayments();
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to record payment";
      alert(message);
    } finally {
      setModalLoading(false);
    }
  };

  // ─── Helpers ─────────────────────────────────
  const formatStatus = (status) => status ? status.replace(/_/g, " ") : "-";

  // ✅ Check if row is overpaid
  const isOverpaid = (p) => parseFloat(p.amountPaid) > parseFloat(p.totalAmountPayable);

  // ─── UI ──────────────────────────────────────
  return (
    <div className="eng-page">

      {/* HEADER */}
      <div className="eng-topbar">
        <h2 className="eng-page-title">Engineer Payments</h2>
      </div>

      {/* ACTION BAR */}
      <div className="eng-table-card" style={{ marginBottom: "20px" }}>
        <div className="eng-table-header">
          <input
            type="date"
            value={paymentMonth}
            onChange={(e) => setPaymentMonth(e.target.value)}
            className="eng-search"
          />
          <button
            className="eng-btn-primary"
            onClick={generatePayments}
            disabled={loading}
          >
            {loading ? "Generating..." : "Generate Payments"}
          </button>
         
        </div>
      </div>

      {/* SUMMARY */}
      {data && (
        <div className="eng-stats">
          <div className="eng-stat-card">
            <div className="eng-stat-label">Total Engineers</div>
            <div className="eng-stat-value">{data.totalEngineers}</div>
          </div>
          <div className="eng-stat-card">
            <div className="eng-stat-label">Total Payable</div>
            <div className="eng-stat-value">₹ {data.totalAmountPayable}</div>
          </div>
          <div className="eng-stat-card">
            <div className="eng-stat-label">Total Paid</div>
            <div className="eng-stat-value">₹ {data.totalAmountPaid}</div>
          </div>
          <div className="eng-stat-card">
            <div className="eng-stat-label">Pending</div>
            <div className="eng-stat-value">₹ {data.totalPendingAmount}</div>
          </div>
        </div>
      )}

      {/* TABLE */}
      {data && (
        <div className="eng-table-card">
          <div className="eng-table-header">
            <span className="eng-table-title">Engineer Payment Details</span>
          </div>
          <table className="eng-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Work Days</th>
                <th>Salary/Day</th>
                <th>Payable</th>
                <th>Paid</th>
                <th>Pending</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.engineerPayments?.map((p) => {
                const overpaid = isOverpaid(p);
                return (
                  <tr
                    key={p.paymentId}
                    style={overpaid ? { background: "#fff5f5" } : {}}
                  >
                    <td>{p.engineerName}</td>
                    <td>{p.daysWorked}</td>
                    <td>₹ {p.salaryPerDay}</td>

                    {/* Payable — red if overpaid */}
                    <td style={overpaid ? { color: "#e53935", fontWeight: 600 } : {}}>
                      ₹ {p.totalAmountPayable}
                    </td>

                    {/* Paid — red if overpaid */}
                    <td style={overpaid ? { color: "#e53935", fontWeight: 600 } : {}}>
                      ₹ {p.amountPaid}
                      {overpaid && (
                        <span style={{
                          marginLeft: "6px", fontSize: "10px",
                          background: "#fdecea", color: "#c62828",
                          padding: "2px 6px", borderRadius: "4px",
                          fontWeight: 600,
                        }}>
                          OVERPAID
                        </span>
                      )}
                    </td>

                    {/* Pending — red if overpaid */}
                    <td style={overpaid ? { color: "#e53935", fontWeight: 600 } : {}}>
                      ₹ {p.pendingAmount}
                    </td>

                    <td>
                      <span
                        className={`eng-badge ${
                          p.paymentStatus === "PAID"
                            ? "eng-badge-active"
                            : "eng-badge-inactive"
                        }`}
                        style={overpaid ? {
                          background: "#fdecea",
                          color: "#c62828",
                          border: "1px solid #ef9a9a",
                        } : {}}
                      >
                        {overpaid ? "OVERPAID" : formatStatus(p.paymentStatus)}
                      </span>
                    </td>

                    <td style={{ display: "flex", gap: "6px" }}>
                      <button
                        className="eng-btn-primary"
                        onClick={() => openPaidModal(p)}
                        disabled={p.paymentStatus === "PAID" || overpaid}
                      >
                        Paid
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* CHECK SALARY MODAL */}
      {salaryModal && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 999,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setSalaryModal(false); }}
        >
          <div
            style={{
              background: "#fff", borderRadius: "12px",
              padding: "1.5rem", width: "95%", maxWidth: "720px",
              maxHeight: "80vh", overflowY: "auto",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <div>
                <h3 style={{ margin: 0 }}>Engineer Salaries</h3>
                <p style={{ fontSize: "13px", color: "#666", margin: "2px 0 0" }}>
                  {paymentMonth.slice(0, 7)}
                </p>
              </div>
              <button className="eng-btn-secondary" onClick={() => setSalaryModal(false)}>
                Close
              </button>
            </div>

            {salaryLoading && (
              <div style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
                Loading salary data...
              </div>
            )}

            {!salaryLoading && salaryData.length === 0 && (
              <div style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
                No salary records found for this month.
              </div>
            )}

            {!salaryLoading && salaryData.length > 0 && (
              <table className="eng-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Work Days</th>
                    <th>Salary/Day</th>
                    <th>Payable</th>
                    <th>Paid</th>
                    <th>Pending</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {salaryData.map((s) => {
                    const over = parseFloat(s.amountPaid) > parseFloat(s.totalAmountPayable);
                    return (
                      <tr key={s.paymentId} style={over ? { background: "#fff5f5" } : {}}>
                        <td>{s.engineerName}</td>
                        <td>{s.daysWorked}</td>
                        <td>₹ {s.salaryPerDay}</td>
                        <td style={over ? { color: "#e53935", fontWeight: 600 } : {}}>₹ {s.totalAmountPayable}</td>
                        <td style={over ? { color: "#e53935", fontWeight: 600 } : {}}>₹ {s.amountPaid}</td>
                        <td style={over ? { color: "#e53935", fontWeight: 600 } : {}}>₹ {s.pendingAmount}</td>
                        <td>
                          <span
                            className={`eng-badge ${s.paymentStatus === "PAID" ? "eng-badge-active" : "eng-badge-inactive"}`}
                            style={over ? { background: "#fdecea", color: "#c62828", border: "1px solid #ef9a9a" } : {}}
                          >
                            {over ? "OVERPAID" : formatStatus(s.paymentStatus)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* PAID MODAL */}
      {modal && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 999,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closePaidModal(); }}
        >
          <div
            style={{
              background: "#fff", borderRadius: "12px",
              padding: "1.5rem", width: "100%", maxWidth: "400px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
          >
            <h3 style={{ margin: "0 0 4px" }}>Record Payment</h3>
            <p style={{ fontSize: "13px", color: "#666", margin: "0 0 1rem" }}>
              {modal.engineerName} &nbsp;·&nbsp; {paymentMonth.slice(0, 7)}
            </p>

            <div
              style={{
                display: "grid", gridTemplateColumns: "1fr 1fr",
                gap: "10px", background: "#f5f5f5",
                borderRadius: "8px", padding: "12px", marginBottom: "1rem",
              }}
            >
              <div>
                <p style={{ fontSize: "11px", color: "#888", margin: "0 0 2px" }}>Payable</p>
                <p style={{ fontSize: "15px", fontWeight: 500, margin: 0 }}>
                  ₹ {modal.totalAmountPayable}
                </p>
              </div>
              <div>
                <p style={{ fontSize: "11px", color: "#888", margin: "0 0 2px" }}>Pending</p>
                <p style={{ fontSize: "15px", fontWeight: 500, color: "#e53935", margin: 0 }}>
                  ₹ {modal.pendingAmount}
                </p>
              </div>
            </div>

            <label style={{ fontSize: "13px", color: "#555", display: "block", marginBottom: "4px" }}>
              Amount to Pay (₹)
            </label>
            <input
              type="number"
              placeholder={`Max: ₹ ${modal.pendingAmount}`}
              value={amountPaid}
              onChange={(e) => setAmountPaid(e.target.value)}
              max={modal.pendingAmount}
              min={1}
              style={{
                width: "100%", marginBottom: "4px", boxSizing: "border-box",
                // ✅ Red border if amount exceeds pending
                border: amountPaid && parseFloat(amountPaid) > parseFloat(modal.pendingAmount)
                  ? "1.5px solid #e53935"
                  : "",
              }}
              className="eng-search"
            />

            {/* ✅ Inline warning */}
            {amountPaid && parseFloat(amountPaid) > parseFloat(modal.pendingAmount) && (
              <p style={{ fontSize: "12px", color: "#e53935", margin: "0 0 10px" }}>
                Amount exceeds pending balance of ₹ {modal.pendingAmount}
              </p>
            )}
            {!amountPaid || parseFloat(amountPaid) <= parseFloat(modal.pendingAmount) ? (
              <div style={{ marginBottom: "10px" }} />
            ) : null}

            <label style={{ fontSize: "13px", color: "#555", display: "block", marginBottom: "4px" }}>
              Remarks (optional)
            </label>
            <textarea
              placeholder="Add a note..."
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              style={{
                width: "100%", boxSizing: "border-box", resize: "none",
                padding: "8px", borderRadius: "6px", border: "1px solid #ddd",
                fontSize: "14px", marginBottom: "1rem",
              }}
            />

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                className="eng-btn-secondary"
                style={{ flex: 1 }}
                onClick={closePaidModal}
                disabled={modalLoading}
              >
                Cancel
              </button>
              <button
                className="eng-btn-primary"
                style={{ flex: 1 }}
                onClick={handleRecordPayment}
                disabled={
                  modalLoading ||
                  !amountPaid ||
                  parseFloat(amountPaid) > parseFloat(modal.pendingAmount) ||
                  parseFloat(amountPaid) <= 0
                }
              >
                {modalLoading ? "Submitting..." : "Submit Payment"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default EngineerPayment;